import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface Beehiiv {
  _id: string;
  apiKey?: string;
  publicationId?: string;
}

export function SettingsBeehiiv() {
  const [beehiiv, setBeehiiv] = useState<Beehiiv | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingBeehiiv, setIsCreatingBeehiiv] = useState(false);
  const [isDeletingBeehiiv, setIsDeletingBeehiiv] = useState(false);
  const [isSavingBeehiiv, setIsSavingBeehiiv] = useState(false);
  const [showBeehiivApiKey, setShowBeehiivApiKey] = useState(false);
  const [beehiivApiKey, setBeehiivApiKey] = useState("");
  const [beehiivPublicationId, setBeehiivPublicationId] = useState("");

  useEffect(() => {
    async function fetchBeehiiv() {
      setIsLoading(true);
      try {
        const beehiivResponse = await fetch("/api/beehiiv");
        if (beehiivResponse.ok) {
          const beehiivData = await beehiivResponse.json();
          if (beehiivData.beehiiv) {
            setBeehiiv(beehiivData.beehiiv);
            setBeehiivApiKey(beehiivData.beehiiv.apiKey || "");
            setBeehiivPublicationId(beehiivData.beehiiv.publicationId || "");
          }
        } else if (beehiivResponse.status !== 404) {
          console.error("Failed to fetch Beehiiv integration");
        }
      } catch (error) {
        console.error("Failed to fetch Beehiiv integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchBeehiiv();
  }, []);

  const handleCreateBeehiiv = async () => {
    if (!beehiivApiKey.trim()) {
      toast.error("API key is required");
      return;
    }

    if (!beehiivPublicationId.trim()) {
      toast.error("Publication ID is required");
      return;
    }

    setIsCreatingBeehiiv(true);
    try {
      const response = await fetch("/api/beehiiv", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          apiKey: beehiivApiKey,
          publicationId: beehiivPublicationId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.beehiiv) {
          setBeehiiv(data.beehiiv);
          setBeehiivApiKey(data.beehiiv.apiKey || "");
          setBeehiivPublicationId(data.beehiiv.publicationId || "");
          toast.success("Beehiiv integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Beehiiv integration");
      }
    } catch (error) {
      console.error("Error creating Beehiiv integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create Beehiiv integration");
    } finally {
      setIsCreatingBeehiiv(false);
    }
  };

  const handleSaveBeehiiv = async () => {
    if (!beehiiv) return;

    setIsSavingBeehiiv(true);
    try {
      const response = await fetch("/api/beehiiv", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          apiKey: beehiivApiKey,
          publicationId: beehiivPublicationId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.beehiiv) {
          setBeehiiv(data.beehiiv);
          toast.success("Beehiiv settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Beehiiv settings");
      }
    } catch (error) {
      console.error("Error saving Beehiiv settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Beehiiv settings");
    } finally {
      setIsSavingBeehiiv(false);
    }
  };

  const handleDeleteBeehiiv = async () => {
    if (!beehiiv) return;

    if (!confirm("Are you sure you want to delete your Beehiiv integration?")) {
      return;
    }

    setIsDeletingBeehiiv(true);
    try {
      const response = await fetch(`/api/beehiiv/${beehiiv._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setBeehiiv(null);
        setBeehiivApiKey("");
        setBeehiivPublicationId("");
        toast.success("Beehiiv integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete Beehiiv integration");
      }
    } catch (error) {
      console.error("Error deleting Beehiiv integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete Beehiiv integration");
    } finally {
      setIsDeletingBeehiiv(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">Beehiiv</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Beehiiv account to add newsletter subscribers from flows
            </p>
            {beehiiv && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
              </div>
            )}
          </div>
          {beehiiv && (
            <Button onClick={handleDeleteBeehiiv} disabled={isDeletingBeehiiv} variant="destructive">
              {isDeletingBeehiiv ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        <div className="space-y-2 pt-4 border-t">
          <div className="space-y-2">
            <Label htmlFor="beehiivApiKey">API Key</Label>
            <div className="relative">
              <Input
                id="beehiivApiKey"
                type={showBeehiivApiKey ? "text" : "password"}
                value={beehiivApiKey}
                onChange={(e) => setBeehiivApiKey(e.target.value)}
                placeholder="Enter Beehiiv API key"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowBeehiivApiKey(!showBeehiivApiKey)}
              >
                {showBeehiivApiKey ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="beehiivPublicationId">Publication ID</Label>
            <Input
              id="beehiivPublicationId"
              type="text"
              value={beehiivPublicationId}
              onChange={(e) => setBeehiivPublicationId(e.target.value)}
              placeholder="pub_00000000-0000-0000-0000-000000000000"
            />
            <p className="text-xs text-muted-foreground">
              Required for creating subscriptions via the Beehiiv API.
            </p>
          </div>
          {!beehiiv ? (
            <Button onClick={handleCreateBeehiiv} disabled={isCreatingBeehiiv}>
              {isCreatingBeehiiv ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect"
              )}
            </Button>
          ) : (
            <Button onClick={handleSaveBeehiiv} disabled={isSavingBeehiiv}>
              {isSavingBeehiiv ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
